import React, { useState, useEffect } from 'react';
import Textarea from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import submissionService from '../../services/submissionService';
import reviewCriteriaService from '../../services/reviewCriteriaService';
import reviewerService from '../../services/reviewerService';
import { formatDate } from '../../utils/formatters';

const PaperReviewInterface = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('reviews');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams?.get('submissionId');
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [isReviewSubmitted, setIsReviewSubmitted] = useState(false);

  const [reviewForm, setReviewForm] = useState({
    overallScore: 5,
    originalityScore: 5,
    technicalQualityScore: 5,
    clarityScore: 5,
    relevanceScore: 5,
    detailedFeedback: '',
    strengths: '',
    weaknesses: '',
    questionsForAuthors: '',
    decision: 'minor_revision',
    confidenceLevel: 3
  });

  const [reviewCriteria, setReviewCriteria] = useState([]);
  const [criteriaAnswers, setCriteriaAnswers] = useState({});

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
    } else if (submissionId) {
      loadSubmission();
    }
  }, [user, submissionId, navigate]);

  const loadSubmission = async () => {
    try {
      setLoading(true);
      const data = await submissionService?.getById(submissionId);
      setSubmission(data);

      if (data?.pdfUrl) {
        const urlData = await submissionService?.getSubmissionFileUrl(data?.pdfUrl);
        setFileUrl(urlData?.url || '');
      }

      // Check if reviewer has already submitted a review for this paper
      const reviewCheck = await reviewerService?.hasSubmittedReview(submissionId, user?.id);

      if (reviewCheck?.hasReview && !reviewCheck?.isDraft) {
        // Review is already submitted - load it in read-only mode
        setIsReviewSubmitted(true);
        const existingReviewData = await reviewerService?.getReviewForSubmission(submissionId, user?.id);
        setExistingReview(existingReviewData);

        // Populate form with existing data (read-only)
        if (existingReviewData) {
          setReviewForm({
            overallScore: existingReviewData?.overallScore || 5,
            detailedFeedback: existingReviewData?.detailedFeedback || '',
            strengths: existingReviewData?.strengths || '',
            weaknesses: existingReviewData?.weaknesses || '',
            decision: existingReviewData?.decision || 'minor_revision',
            confidenceLevel: existingReviewData?.confidenceLevel || 3
          });
        }
      } else if (reviewCheck?.hasReview && reviewCheck?.isDraft) {
        // Draft exists - load it for editing
        const draftReviewData = await reviewerService?.getReviewForSubmission(submissionId, user?.id);
        setExistingReview(draftReviewData);

        if (draftReviewData) {
          setReviewForm({
            overallScore: draftReviewData?.overallScore || 5,
            detailedFeedback: draftReviewData?.detailedFeedback || '',
            strengths: draftReviewData?.strengths || '',
            weaknesses: draftReviewData?.weaknesses || '',
            decision: draftReviewData?.decision || 'minor_revision',
            confidenceLevel: draftReviewData?.confidenceLevel || 3
          });
        }
      }

      // Load review criteria for this conference
      if (data?.conferenceId) {
        const criteria = await reviewCriteriaService?.getByConference(data?.conferenceId);
        setReviewCriteria(criteria);

        // Initialize answers object
        const initialAnswers = {};
        criteria?.forEach(c => {
          if (c?.questionType === 'scale') {
            initialAnswers[c?.id] = Math.floor(((c?.scaleMin || 1) + (c?.scaleMax || 5)) / 2);
          } else if (c?.questionType === 'true_false') {
            initialAnswers[c?.id] = null;
          } else {
            initialAnswers[c?.id] = '';
          }
        });

        // If loading existing review, populate criteria answers
        if (reviewCheck?.hasReview && existingReview?.criteriaAnswers) {
          const existingAnswers = {};
          existingReview?.criteriaAnswers?.forEach(answer => {
            existingAnswers[answer?.criteriaId] = answer?.value;
          });
          setCriteriaAnswers({...initialAnswers, ...existingAnswers});
        } else {
          setCriteriaAnswers(initialAnswers);
        }
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (field, value) => {
    if (isReviewSubmitted) return; // Prevent editing submitted reviews
    const numValue = parseInt(value);
    if (numValue >= 1 && numValue <= 10) {
      setReviewForm({ ...reviewForm, [field]: numValue });
    }
  };

  const handleCriteriaAnswerChange = (criteriaId, value) => {
    if (isReviewSubmitted) return; // Prevent editing submitted reviews
    setCriteriaAnswers({
      ...criteriaAnswers,
      [criteriaId]: value
    });
  };

  const handleSubmitReview = async () => {
    // Validate required criteria
    const requiredCriteria = reviewCriteria?.filter(c => c?.isRequired);
    for (const criteria of requiredCriteria) {
      const answer = criteriaAnswers?.[criteria?.id];
      if (answer === null || answer === undefined || answer === '') {
        toastError(t('paperReview.reviewForm.validationError', { question: criteria?.questionText }));
        return;
      }
    }

    if (!reviewForm?.detailedFeedback?.trim()) {
      toastError(t('paperReview.reviewForm.commentsRequired'));
      return;
    }

    try {
      setSaving(true);
      
      // Save custom criteria answers
      const answers = reviewCriteria?.map(criteria => ({
        criteriaId: criteria?.id,
        questionType: criteria?.questionType,
        value: criteriaAnswers?.[criteria?.id]
      }));

      await reviewCriteriaService?.saveAnswers(submissionId, user?.id, answers);

      // Save standard review data to submission_reviews
      await reviewerService?.submitReview({
        submissionId,
        reviewerId: user?.id,
        conferenceId: submission?.conferenceId,
        ...reviewForm,
        criteriaAnswers
      });

      toastSuccess(t('paperReview.reviewForm.submitSuccess'));
      navigate('/user-dashboard');
    } catch (err) {
      toastError(`Error: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFile = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderCriteriaQuestion = (criteria) => {
    switch (criteria?.questionType) {
      case 'scale':
        return (
          <div key={criteria?.id} className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              {criteria?.questionText}
              {criteria?.isRequired && <span className="text-error ml-1">*</span>}
            </label>
            {criteria?.helpText && (
              <p className="text-xs text-muted-foreground mb-3">{criteria?.helpText}</p>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{criteria?.scaleMinLabel || criteria?.scaleMin}</span>
                <span className="font-semibold text-primary text-base">
                  {criteriaAnswers?.[criteria?.id] || criteria?.scaleMin}
                </span>
                <span>{criteria?.scaleMaxLabel || criteria?.scaleMax}</span>
              </div>
              <input
                type="range"
                min={criteria?.scaleMin || 1}
                max={criteria?.scaleMax || 5}
                value={criteriaAnswers?.[criteria?.id] || criteria?.scaleMin}
                onChange={(e) => handleCriteriaAnswerChange(criteria?.id, parseInt(e?.target?.value))}
                disabled={isReviewSubmitted}
                className="w-full"
              />
              <div className="flex justify-center gap-2">
                {Array.from({ length: (criteria?.scaleMax || 5) - (criteria?.scaleMin || 1) + 1 }, (_, i) => {
                  const value = (criteria?.scaleMin || 1) + i;
                  return (
                    <button
                      key={i}
                      onClick={() => handleCriteriaAnswerChange(criteria?.id, value)}
                      disabled={isReviewSubmitted}
                      className={`w-10 h-10 rounded-lg border transition-all duration-250 text-sm font-medium ${
                        criteriaAnswers?.[criteria?.id] === value
                          ? 'border-primary bg-primary text-white' :'border-border hover:border-primary hover:bg-primary/10'
                      } ${isReviewSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'true_false':
        return (
          <div key={criteria?.id} className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              {criteria?.questionText}
              {criteria?.isRequired && <span className="text-error ml-1">*</span>}
            </label>
            {criteria?.helpText && (
              <p className="text-xs text-muted-foreground mb-3">{criteria?.helpText}</p>
            )}
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 ${isReviewSubmitted ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name={`criteria-${criteria?.id}`}
                  checked={criteriaAnswers?.[criteria?.id] === true}
                  onChange={() => handleCriteriaAnswerChange(criteria?.id, true)}
                  disabled={isReviewSubmitted}
                  className="w-4 h-4"
                />
                <span className="text-foreground">{t('paperReview.criteriaQuestions.true')}</span>
              </label>
              <label className={`flex items-center gap-2 ${isReviewSubmitted ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name={`criteria-${criteria?.id}`}
                  checked={criteriaAnswers?.[criteria?.id] === false}
                  onChange={() => handleCriteriaAnswerChange(criteria?.id, false)}
                  disabled={isReviewSubmitted}
                  className="w-4 h-4"
                />
                <span className="text-foreground">{t('paperReview.criteriaQuestions.false')}</span>
              </label>
            </div>
          </div>
        );

      case 'short_text':
        return (
          <div key={criteria?.id} className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              {criteria?.questionText}
              {criteria?.isRequired && <span className="text-error ml-1">*</span>}
            </label>
            {criteria?.helpText && (
              <p className="text-xs text-muted-foreground mb-3">{criteria?.helpText}</p>
            )}
            <input
              type="text"
              value={criteriaAnswers?.[criteria?.id] || ''}
              onChange={(e) => handleCriteriaAnswerChange(criteria?.id, e?.target?.value)}
              disabled={isReviewSubmitted}
              placeholder={t('paperReview.criteriaQuestions.shortTextPlaceholder')}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        );

      case 'long_text':
        return (
          <div key={criteria?.id} className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              {criteria?.questionText}
              {criteria?.isRequired && <span className="text-error ml-1">*</span>}
            </label>
            {criteria?.helpText && (
              <p className="text-xs text-muted-foreground mb-3">{criteria?.helpText}</p>
            )}
            <Textarea
              value={criteriaAnswers?.[criteria?.id] || ''}
              onChange={(e) => handleCriteriaAnswerChange(criteria?.id, e?.target?.value)}
              disabled={isReviewSubmitted}
              placeholder={t('paperReview.criteriaQuestions.longTextPlaceholder')}
              rows={4}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('paperReview.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <p className="text-error mb-4">{error}</p>
          <Button onClick={() => navigate('/user-dashboard')}>{t('paperReview.backToDashboard')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="py-5 px-4 sm:px-6 lg:px-8">
        <div className="page-shell">
            <div className="mb-4">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => navigate('/user-dashboard')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                {t('paperReview.back')}
              </Button>
            </div>

            <div className="bg-card rounded-lg border border-border p-5 mb-5">
              <h1 className="text-lg font-semibold text-foreground mb-1">{submission?.title}</h1>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon name="User" size={16} />
                  <span>
                    {submission?.author?.firstName} {submission?.author?.lastName}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Building" size={16} />
                  <span>{submission?.author?.institution}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} />
                  <span>
                    {t('paperReview.submitted', { date: formatDate(submission?.submittedAt) || 'N/A' })}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-foreground">{t('paperReview.paperPreview')}</h2>
                  {fileUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenFile}
                      iconName="ExternalLink"
                      iconPosition="right"
                    >
                      {t('paperReview.openFullPdf')}
                    </Button>
                  )}
                </div>

                {fileUrl ? (
                  <div className="border border-border rounded-lg p-6 bg-muted/20">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="FileText" size={24} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground truncate">
                          {submission?.pdfFileName || 'Submission file'}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Reviewers download files instead of previewing them in the app.
                        </p>
                        <div className="flex items-center gap-3 mt-4">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleOpenFile}
                            iconName="Download"
                            iconPosition="left"
                          >
                            Download File
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleOpenFile}
                            iconName="ExternalLink"
                            iconPosition="right"
                          >
                            Open in New Tab
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-border rounded-lg p-12 text-center">
                    <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t('paperReview.noPdfAvailable')}</p>
                  </div>
                )}

                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-foreground mb-3">{t('paperReview.abstract')}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{submission?.abstract}</p>
                </div>

                {submission?.keywords && submission?.keywords?.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-foreground mb-3">{t('paperReview.keywords')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {submission?.keywords?.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary text-sm"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-6">{t('paperReview.reviewForm.title')}</h2>

                {isReviewSubmitted && (
                  <div className="bg-success/10 border-2 border-success/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <Icon name="CheckCircle" size={24} className="text-success flex-shrink-0" />
                      <div>
                        <h3 className="text-base font-semibold text-success mb-1">{t('paperReview.reviewForm.submitted.title')}</h3>
                        <p className="text-sm text-muted-foreground">
                          {t('paperReview.reviewForm.submitted.message', { date: existingReview?.submittedAt ? formatDate(existingReview?.submittedAt) : '' })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {reviewCriteria?.length > 0 && (
                    <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                      <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                        {t('paperReview.reviewForm.customCriteria')}
                      </h2>
                      <div className="space-y-6">
                        {reviewCriteria?.map(criteria => renderCriteriaQuestion(criteria))}
                      </div>
                    </div>
                  )}

                  <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                    <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                      {t('paperReview.reviewForm.generalComments.title')}
                      <span className="text-error ml-1">{t('paperReview.reviewForm.generalComments.required')}</span>
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('paperReview.reviewForm.generalComments.description')}
                    </p>
                    <Textarea
                      value={reviewForm?.detailedFeedback || ''}
                      onChange={(e) => !isReviewSubmitted && setReviewForm({ ...reviewForm, detailedFeedback: e?.target?.value })}
                      disabled={isReviewSubmitted}
                      placeholder={t('paperReview.reviewForm.generalComments.placeholder')}
                      rows={8}
                    />
                  </div>

                  <div>
                    <Textarea
                      label={t('paperReview.reviewForm.strengths.label')}
                      value={reviewForm?.strengths || ''}
                      onChange={(e) => !isReviewSubmitted && setReviewForm({ ...reviewForm, strengths: e?.target?.value })}
                      disabled={isReviewSubmitted}
                      placeholder={t('paperReview.reviewForm.strengths.placeholder')}
                      rows={4}
                    />
                  </div>

                  <div>
                    <Textarea
                      label={t('paperReview.reviewForm.weaknesses.label')}
                      value={reviewForm?.weaknesses || ''}
                      onChange={(e) => !isReviewSubmitted && setReviewForm({ ...reviewForm, weaknesses: e?.target?.value })}
                      disabled={isReviewSubmitted}
                      placeholder={t('paperReview.reviewForm.weaknesses.placeholder')}
                      rows={4}
                    />
                  </div>

                  <div>
                    <Textarea
                      label={t('paperReview.reviewForm.questionsForAuthors.label')}
                      value={reviewForm?.questionsForAuthors || ''}
                      onChange={(e) => !isReviewSubmitted && setReviewForm({ ...reviewForm, questionsForAuthors: e?.target?.value })}
                      disabled={isReviewSubmitted}
                      placeholder={t('paperReview.reviewForm.questionsForAuthors.placeholder')}
                      rows={4}
                    />
                  </div>

                  {!isReviewSubmitted && (
                    <div className="flex gap-3">
                      <Button
                        onClick={handleSubmitReview}
                        disabled={saving}
                        iconName={saving ? 'Loader' : 'Send'}
                        iconPosition="left"
                        className="flex-1"
                      >
                        {saving ? t('paperReview.reviewForm.submitting') : t('paperReview.reviewForm.submitReview')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/user-dashboard')}
                      >
                        {t('paperReview.reviewForm.cancel')}
                      </Button>
                    </div>
                  )}

                  {isReviewSubmitted && (
                    <Button
                      variant="outline"
                      onClick={() => navigate('/user-dashboard')}
                      iconName="ArrowLeft"
                      iconPosition="left"
                      fullWidth
                    >
                      {t('paperReview.backToDashboard')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
    </div>
  );
};

export default PaperReviewInterface;
